import React, { useState } from "react";
import { TableCell, TableRow, TableSortLabel, TablePagination } from "@mui/material";
import { stableSort } from "../../../../common-utilities/sortingUtils";

const DEFAULT_ROWS_PER_PAGE = [10, 25, 50, 100, 200];

/**
 * ENB-584: client-side sort and paging for the tracker tables, same behavior as Supply Orders.
 * Columns are { label, field, rank? }: a column without field is not sortable (Actions);
 * rank(value) gives a custom order (e.g. reorder status by urgency).
 * Blanks always sink to the bottom; text compares case-insensitively and numerically.
 */
const useSortedPaging = (rows, columns, { rowsPerPageOptions = DEFAULT_ROWS_PER_PAGE, defaultOrderBy = "" } = {}) => {
  const [orderBy, setOrderBy] = useState(defaultOrderBy);
  const [sortOrder, setSortOrder] = useState("asc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);

  const requestSort = (field) => {
    const isAsc = orderBy === field && sortOrder === "asc";
    setSortOrder(isAsc ? "desc" : "asc");
    setOrderBy(field);
    setPage(0);
  };

  const rank = columns.find((c) => c.field === orderBy)?.rank;

  const compare = (a, b) => {
    let x = a[orderBy];
    let y = b[orderBy];
    const xBlank = x === null || x === undefined || x === "";
    const yBlank = y === null || y === undefined || y === "";
    if (xBlank || yBlank) {
      return xBlank === yBlank ? 0 : xBlank ? 1 : -1;
    }

    if (rank) {
      x = rank(x);
      y = rank(y);
    }

    const result =
      typeof x === "number" && typeof y === "number"
        ? x - y
        : String(x).localeCompare(String(y), undefined, { numeric: true, sensitivity: "base" });
    return sortOrder === "desc" ? -result : result;
  };

  // Sort the whole result set, not just the visible page, since everything is loaded client side.
  const sorted = orderBy ? stableSort(rows, compare) : rows;

  // A filter that shrinks the list can leave the page past the end, so fall back to the first page.
  const safePage = page * rowsPerPage >= sorted.length ? 0 : page;
  const pageRows = sorted.slice(safePage * rowsPerPage, safePage * rowsPerPage + rowsPerPage);

  const header = (
    <TableRow>
      {columns.map((column) =>
        column.field ? (
          <TableCell key={column.label} sortDirection={orderBy === column.field ? sortOrder : false}>
            <TableSortLabel
              active={orderBy === column.field}
              direction={orderBy === column.field ? sortOrder : "asc"}
              onClick={() => requestSort(column.field)}
            >
              {column.label}
            </TableSortLabel>
          </TableCell>
        ) : (
          <TableCell key={column.label}>{column.label}</TableCell>
        )
      )}
    </TableRow>
  );

  const pagination = (
    <TablePagination
      rowsPerPageOptions={rowsPerPageOptions}
      component="div"
      count={sorted.length}
      rowsPerPage={rowsPerPage}
      page={safePage}
      onPageChange={(e, newPage) => setPage(newPage)}
      onRowsPerPageChange={(e) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
      }}
    />
  );

  return { header, pageRows, pagination, resetPage: () => setPage(0) };
};

export default useSortedPaging;
